import PropTypes from "prop-types";
import {
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Stack,
  CircularProgress,
} from "@mui/material";
import AuthShell from "../../components/NewLogin/AuthShell";
import AuthFormWrapper from "../../components/NewLogin/AuthFormWrapper";
import AuthTransition from "../../components/NewLogin/AuthTransition";
import BackToLogin from "../../components/NewLogin/BackToLogin";
import { FONT, FONT_REGULAR, labelSx, inputSx } from "../../components/NewLogin/authStyles";
import { useTranslation } from "react-i18next";
import images from "../../constant/images";
import SendIcon from "../../components/NewLogin/SendIcon";

/**
 * ForgotPasswordPage
 *
 * Props:
 *   forgotForm     – { email }
 *   errors         – { email? }
 *   isSubmitting   – boolean
 *   onFieldChange  – (field) => (event) => void
 *   onSubmit       – () => void
 *   onBackToLogin  – () => void
 *   onDidntReceive – () => void
 */
const ForgotPasswordPage = ({
  forgotForm,
  errors,
  isSubmitting,
  onFieldChange,
  onSubmit,
  onBackToLogin,
  onDidntReceive,
}) => {
  const { t } = useTranslation();
  return (
    <AuthShell isLoading>
      <AuthFormWrapper maxWidth={300} maxHeight={500}>
        <AuthTransition>
          {/* ── SCAI logo + heading ── */}
          <Stack alignItems="center" spacing="0.276rem" sx={{ mb: "1.857rem" }} gap={"1.429rem"}>
            <Box component="img" src={images.SCAI} />

            <Stack alignItems="center" spacing="0.276rem">
              <Typography
                variant="h1"
                sx={{
                  fontFamily: FONT,
                  fontSize: "1.8rem",
                  fontWeight: 400,
                  lineHeight: "1.857rem",
                  color: "#07080B",
                  textAlign: "center",
                  textWrapStyle: "pretty"
                }}
              >
                {t("RestoreAccountAccess")} 🔧
              </Typography>
              <Typography
                sx={{
                  fontFamily: FONT_REGULAR,
                  fontSize: "1.143rem",
                  fontWeight: 300,
                  lineHeight: "1.714rem",
                  color: "#474849",
                  textAlign: "center",
                }}
              >
                {t("NO_WORRIES")}
              </Typography>
            </Stack>
          </Stack>

          {/* ── Form ── */}
          <Box sx={{ mb: "0.505rem" }}>
            <Typography component="label" htmlFor="forgot-email" sx={labelSx}>
              {t("Email")}
            </Typography>
            <TextField
              id="forgot-email"
              fullWidth
              size="small"
              placeholder={t("enterEmailAddressPlaceholder")}
              value={forgotForm.email}
              onChange={onFieldChange("email")}
              error={!!errors}
              helperText={t(errors)}
              autoComplete="email"
              sx={inputSx}
              slotProps={{ input: { "aria-label": "Email address" } }}
            />
          </Box>

          {/* ── Didn't Receive? ── */}
          <Box sx={{ textAlign: "right", mb: "1.2rem" }}>
            <Link
              component="button"
              onClick={onDidntReceive}
              underline="none"
              sx={{
                fontFamily: FONT,
                fontSize: "1.048rem",
                fontWeight: 300,
                lineHeight: "1.333rem",
                color: "#0166FF",
                // cursor: "pointer",
                // "&:hover": { textDecoration: "underline" },
              }}
            >

            </Link>
          </Box>

          {/* ── Request Reset – primary button ── */}
          <Box
            sx={{
              border: "0.095rem solid #07080B",
              borderRadius: "0.190rem",
              padding: "1px",
              boxSizing: "border-box",
              mb: "1.429rem",
              "&:hover": { border: "0.095rem solid rgb(233, 79, 28) !important" }
            }}
          >
            <Button
              fullWidth
              onClick={onSubmit}
              disabled={isSubmitting}
              aria-label="Request password reset"
              endIcon={<SendIcon />}
              sx={{
                fontFamily: FONT_REGULAR,
                fontSize: "1.043rem",
                fontWeight: 300,
                lineHeight: "1.524rem",
                color: "#FFFFFF",
                backgroundColor: "#07080B",
                borderRadius: "0.190rem",
                height: "3rem",
                padding: "0.952rem 2.286rem",
                gap: "0.001rem",
                textTransform: "none",
                "&:hover": { backgroundColor: "rgb(233, 79, 28)", color: "#ffffff" },
                "&.Mui-disabled": { backgroundColor: "#07080B", color: "#FFFFFF", opacity: 0.6 },
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={"1.524rem"} sx={{ color: "#fff" }} />
              ) : (
                t("Request_Reset")
              )}
            </Button>
          </Box>

          {/* ── Back to login ── */}
          <BackToLogin onBackToLogin={onBackToLogin} />
        </AuthTransition>
      </AuthFormWrapper>
    </AuthShell>
  );
};

ForgotPasswordPage.propTypes = {
  forgotForm: PropTypes.object,
  errors: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  isSubmitting: PropTypes.bool,
  onFieldChange: PropTypes.func,
  onSubmit: PropTypes.func,
  onBackToLogin: PropTypes.func,
  onDidntReceive: PropTypes.func,
};

export default ForgotPasswordPage;
